import { actions, ActorRefFrom, assign, createMachine, InterpreterFrom, StateMachine } from "xstate"
import { assertEvent } from "../xstate-helpers"
import { getErrorCounter, incrementErrorCounter } from "../error-counter"

export type NotificationContext = {
    kind?: ErrorKind | NotificationKind
    origin?: string
    errorCode?: string
    info?: unknown
    message?: string
}

type ClosedStateContext = {
    // NOTE: this is intended to be empty
}

export type NotificationStateContext = {
    kind: NotificationKind
    origin: string
    info: unknown
}

export type ErrorStateContext = {
    kind: ErrorKind
    origin: string
    errorCode: string
    info: unknown
    message: string
    errorCounter?: number
}

type NotificationTypeState =
    | { value: "closed"; context: ClosedStateContext }
    | { value: "notification"; context: NotificationStateContext }
    | { value: "error"; context: ErrorStateContext }

export type ErrorKind = "generic" | "fatal" | "createQuote"
export type NotificationKind =
    | "cannotReload"
    | "selectedSuiteNoMoreAvailable"
    | "cannotSelectSuite"
    | "invalidCaptcha"
    | "noAvailability"
    | "invalidParameter"
    | "checkoutProcessExpired"
type ErrorEvent = { type: "ERROR"; kind: ErrorKind; origin: string; errorCode: string; info: unknown; message: string }
type NotifyEvent = { type: "NOTIFY"; kind: NotificationKind; origin: string; info: unknown }
type CloseEvent = { type: "CLOSE" }
export type NotificationEvent = ErrorEvent | NotifyEvent | CloseEvent

const config = {
    id: "notification",
    initial: "closed" as const,
    predictableActionArguments: true,
    context: {},
    states: {
        closed: {
            on: {
                ERROR: { target: "error", actions: ["incrementErrorCounter", "setError"] },
                NOTIFY: { target: "notification", actions: ["setNotification"] },
            },
        },
        notification: {
            on: {
                CLOSE: { target: "closed" },
            },
        },
        error: {},
    },
}

const options = {
    actions: {
        incrementErrorCounter: () => {
            incrementErrorCounter()
        },
        setError: assign<NotificationContext, NotificationEvent>((_: NotificationContext, ev: NotificationEvent) => {
            assertEvent(ev, "ERROR")
            const errorCounter = getErrorCounter()
            const { kind, origin, errorCode, info, message } = ev
            return { kind, origin, errorCode, info, message, errorCounter }
        }),
        setNotification: assign<NotificationContext, NotificationEvent>(
            (_: NotificationContext, ev: NotificationEvent) => {
                assertEvent(ev, "NOTIFY")
                const { kind, origin, info } = ev
                return { kind, origin, info }
            },
        ),
        logMe: actions.log<NotificationContext, NotificationEvent>((ctx, ev) => ({ type: ev.type, ctx, ev })),
    },
}

export default createMachine<NotificationContext, NotificationEvent>(config, options)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NotificationStateMachine = StateMachine<NotificationContext, any, NotificationEvent, NotificationTypeState>
export type NotificationActor = ActorRefFrom<NotificationStateMachine>
export type NotificationService = InterpreterFrom<NotificationStateMachine>
export type NotificationState = NotificationService["state"]
export type NotificationSend = NotificationService["send"]
